// src/lib/api/invitationApi.ts

import { getApiClient } from './client';
import {
    Invitation,
    InvitationListResponse,
    SendInvitationRequest,
    BatchSendInvitationRequest,
    BatchSendInvitationResponse,
    InvitationInfo,
} from '../models/invitation';

const sendInvitation = async (data: SendInvitationRequest): Promise<Invitation> => {
    return await getApiClient().post<Invitation>('/api/invitations', data);
};

// Bulk invite: send the same message to a list of emails in one request. Returns
// a per-email result (sent / already-member / invalid / …) plus tallies.
const sendInvitationsBatch = async (
    data: BatchSendInvitationRequest,
): Promise<BatchSendInvitationResponse> => {
    return await getApiClient().post<BatchSendInvitationResponse>('/api/invitations/batch', data);
};

const getMyInvitations = async (page: number = 1, limit: number = 20): Promise<InvitationListResponse> => {
    try {
        return await getApiClient().get<InvitationListResponse>('/api/invitations', {
            params: { page, limit }
        });
    } catch {
        return { invitations: [], total: 0 };
    }
};

const cancelInvitation = async (id: string): Promise<void> => {
    await getApiClient().delete(`/api/invitations/${id}`);
};

const resendInvitation = async (id: string): Promise<Invitation> => {
    return await getApiClient().post<Invitation>(`/api/invitations/${id}/resend`, {});
};

const getReceivedInvitations = async (): Promise<InvitationListResponse> => {
    try {
        return await getApiClient().get<InvitationListResponse>('/api/my/invitations');
    } catch {
        return { invitations: [], total: 0 };
    }
};

const getInvitationByToken = async (token: string): Promise<InvitationInfo> => {
    return await getApiClient().get<InvitationInfo>(`/api/invitations/accept/${token}`);
};

const acceptInvitation = async (token: string): Promise<Invitation> => {
    return await getApiClient().post<Invitation>(`/api/invitations/accept/${token}`, {});
};

// #355 — set the password on an admin-created employer account.
//
// publicPost, not post: the recipient has no password yet, so there is no
// session and no bearer token to attach. Sending one via the authed client
// would be harmless but misleading — this endpoint is reached before login by
// design.
const activateEmployerInvitation = async (token: string, password: string): Promise<{ message: string }> => {
    return await getApiClient().publicPost<{ message: string }>(
        `/api/invitations/activate/${token}`,
        { password }
    );
};

// Accept an invitation in-app as the logged-in recipient (no email token).
// The backend verifies the invitation is addressed to the current user.
const acceptInvitationById = async (id: string): Promise<Invitation> => {
    return await getApiClient().post<Invitation>(`/api/invitations/${id}/accept`, {});
};

export const invitationApi = {
    sendInvitation,
    sendInvitationsBatch,
    getMyInvitations,
    cancelInvitation,
    resendInvitation,
    getReceivedInvitations,
    getInvitationByToken,
    activateEmployerInvitation,
    acceptInvitation,
    acceptInvitationById,
};
